import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import LayerRadar from "@/components/n360/LayerRadar";
import { GradeBadge, AccessBadge } from "@/components/n360/Badges";

const DOSSIER_MODULES = [
  "Comprehensive Biography", "Institutional Governance & Board Mandates", "Verified Track Record",
  "Network Topography", "Strategic Opportunities", "Knowledge Vault",
  "Bilateral Communication Channels", "Compliance & Risk", "Public Profile", "Identity & Origin",
];

const inputCls = "w-full px-3 py-2 rounded-lg bg-[#F6EEDC] border border-[#F1551D]/20 text-[#0D3C4C] placeholder:text-[#aab9bd] focus:outline-none focus:border-[#F1551D]/60 text-sm";

function TagEditor({ label, values, onChange, tone }) {
  const [v, setV] = useState("");
  const add = () => { if (v.trim()) { onChange([...(values || []), v.trim()]); setV(""); } };
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-[#8598a0] mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {(values || []).map((t, i) => (
          <span key={i} className={`text-xs px-2 py-1 rounded-full border flex items-center gap-1 ${tone === "emerald" ? "border-emerald-500/30 text-emerald-300" : "border-[#F1551D]/30 text-[#B84214]"}`}>
            {t} <button onClick={() => onChange(values.filter((_, j) => j !== i))}><Icons.X className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} className={inputCls} placeholder="Add and press Enter" />
        <button onClick={add} className="px-3 rounded-lg border border-[#F1551D]/30 text-[#F1551D] text-sm">Add</button>
      </div>
    </div>
  );
}

export default function ProfileBuilder() {
  const { user, meta } = useAuth();
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newFact, setNewFact] = useState({ module: DOSSIER_MODULES[0], label: "", value: "", source: "", confidence: 80, access_level: "Member" });

  const layers = meta?.layers || [];
  const pillars = meta?.pillars || [];
  const accessLevels = meta?.access_levels || [];
  const subSectors = pillars.find((x) => x.id === p?.pillar)?.subSectors || [];

  useEffect(() => { api.get("/profile/me").then(({ data }) => setP(data)); }, []);

  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v, ...(k === "pillar" ? { sub_sector: "" } : {}) }));
  const setLayer = (id, v) => setP((prev) => ({ ...prev, layer_scores: { ...(prev.layer_scores || {}), [id]: Number(v) } }));

  const addFact = () => {
    if (!newFact.label || !newFact.value) { toast.error("Fact needs a label and value."); return; }
    setP((prev) => ({ ...prev, facts: [...(prev.facts || []), { ...newFact, id: crypto.randomUUID() }] }));
    setNewFact({ ...newFact, label: "", value: "", source: "" });
  };
  const removeFact = (id) => setP((prev) => ({ ...prev, facts: prev.facts.filter((f) => f.id !== id) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        headline: p.headline, avatar_url: p.avatar_url, pillar: p.pillar, sub_sector: p.sub_sector,
        country: p.country, organization: p.organization, bio: p.bio, summary: p.summary || {},
        layer_scores: p.layer_scores || {}, facts: p.facts || [], seeking: p.seeking || [], offering: p.offering || [],
      };
      const { data } = await api.put("/profile/me", payload);
      setP(data);
      toast.success("Dossier updated.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  if (!p) return <div className="text-center py-24 text-[#8598a0] rise">Loading your dossier…</div>;

  return (
    <div className="rise pb-10">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <div className="font-mono-i text-[11px] tracking-[0.3em] text-[#F1551D]/90 uppercase mb-2">360° Dossier Builder</div>
          <h1 className="font-display text-3xl text-[#0D3C4C]">My Intelligence Dossier</h1>
        </div>
        <div className="flex items-center gap-3">
          <GradeBadge grade={user?.grade} /><AccessBadge level={user?.access_level} />
          <button onClick={save} disabled={saving} data-testid="profile-save-btn" className="px-6 py-2.5 rounded-full bg-[#F1551D] text-[#FEF7E7] font-medium hover:bg-[#F0A315] disabled:opacity-60">{saving ? "Saving…" : "Save Dossier"}</button>
        </div>
      </div>

      <div className="mb-6 n360-card p-4">
        <div className="flex items-center justify-between text-sm mb-2"><span className="text-[#5f7d86]">Completeness</span><span className="text-[#B84214] font-mono-i">{p.completeness}%</span></div>
        <div className="h-2 rounded-full bg-[#F6EEDC] overflow-hidden"><div className="h-full bg-gradient-to-r from-[#F1551D] to-[#F0A315] transition-all" style={{ width: `${p.completeness}%` }} /></div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Core */}
        <div className="n360-card p-6 space-y-4">
          <h3 className="font-display text-lg text-[#0D3C4C]">Core Identity</h3>
          <div><div className="text-[11px] uppercase tracking-wider text-[#8598a0] mb-1.5">Headline</div><input data-testid="pb-headline" value={p.headline || ""} onChange={(e) => set("headline", e.target.value)} className={inputCls} placeholder="e.g. Managing Director, …" /></div>
          <div><div className="text-[11px] uppercase tracking-wider text-[#8598a0] mb-1.5">Avatar URL</div><input data-testid="pb-avatar" value={p.avatar_url || ""} onChange={(e) => set("avatar_url", e.target.value)} className={inputCls} placeholder="https://…" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><div className="text-[11px] uppercase tracking-wider text-[#8598a0] mb-1.5">Organization</div><input data-testid="pb-org" value={p.organization || ""} onChange={(e) => set("organization", e.target.value)} className={inputCls} /></div>
            <div><div className="text-[11px] uppercase tracking-wider text-[#8598a0] mb-1.5">Country</div><input data-testid="pb-country" value={p.country || ""} onChange={(e) => set("country", e.target.value)} className={inputCls} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#8598a0] mb-1.5">Pillar</div>
              <select data-testid="pb-pillar" value={p.pillar || ""} onChange={(e) => set("pillar", e.target.value)} className={inputCls}>
                <option value="" className="bg-[#F6EEDC]">Select…</option>
                {pillars.map((x) => <option key={x.id} value={x.id} className="bg-[#F6EEDC]">{x.name}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#8598a0] mb-1.5">Sub-Sector</div>
              <select data-testid="pb-subsector" value={p.sub_sector || ""} onChange={(e) => set("sub_sector", e.target.value)} className={inputCls} disabled={!subSectors.length}>
                <option value="" className="bg-[#F6EEDC]">Select…</option>
                {subSectors.map((s) => <option key={s} value={s} className="bg-[#F6EEDC]">{s}</option>)}
              </select>
            </div>
          </div>
          <div><div className="text-[11px] uppercase tracking-wider text-[#8598a0] mb-1.5">Biography</div><textarea data-testid="pb-bio" value={p.bio || ""} onChange={(e) => set("bio", e.target.value)} rows={4} className={inputCls} /></div>
          <TagEditor label="Seeking" values={p.seeking} onChange={(v) => set("seeking", v)} tone="emerald" />
          <TagEditor label="Offering" values={p.offering} onChange={(v) => set("offering", v)} tone="gold" />
        </div>

        {/* Layers */}
        <div className="n360-card p-6">
          <h3 className="font-display text-lg text-[#0D3C4C] mb-4">8-Layer Intelligence Calibration</h3>
          <LayerRadar layers={layers} scores={p.layer_scores || {}} height={240} />
          <div className="space-y-3 mt-4">
            {layers.map((l) => (
              <div key={l.id} className="flex items-center gap-3">
                <span className="font-mono-i text-[10px] text-[#F1551D] w-12">{l.code}</span>
                <span className="text-xs text-[#5f7d86] flex-1 truncate">{l.name}</span>
                <input data-testid={`pb-layer-${l.id}`} type="range" min={0} max={100} value={p.layer_scores?.[l.id] || 0} onChange={(e) => setLayer(l.id, e.target.value)} className="w-32 accent-[#F1551D]" />
                <span className="font-mono-i text-xs text-[#2f5561] w-8 text-right">{p.layer_scores?.[l.id] || 0}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Facts */}
      <div className="n360-card p-6 mt-6">
        <h3 className="font-display text-lg text-[#0D3C4C] mb-4">Dossier Facts <span className="text-sm text-[#8598a0]">— each with source, confidence & clearance</span></h3>
        <div className="grid md:grid-cols-6 gap-2 mb-4">
          <select value={newFact.module} onChange={(e) => setNewFact({ ...newFact, module: e.target.value })} className={`${inputCls} md:col-span-2`} data-testid="fact-module">
            {DOSSIER_MODULES.map((m) => <option key={m} value={m} className="bg-[#F6EEDC]">{m}</option>)}
          </select>
          <input value={newFact.label} onChange={(e) => setNewFact({ ...newFact, label: e.target.value })} placeholder="Label" className={inputCls} data-testid="fact-label" />
          <input value={newFact.value} onChange={(e) => setNewFact({ ...newFact, value: e.target.value })} placeholder="Value" className={`${inputCls} md:col-span-2`} data-testid="fact-value" />
          <input value={newFact.source} onChange={(e) => setNewFact({ ...newFact, source: e.target.value })} placeholder="Source" className={inputCls} data-testid="fact-source" />
        </div>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="flex items-center gap-2"><span className="text-xs text-[#8598a0]">Confidence</span><input type="range" min={0} max={100} value={newFact.confidence} onChange={(e) => setNewFact({ ...newFact, confidence: Number(e.target.value) })} className="accent-[#F1551D]" data-testid="fact-confidence" /><span className="font-mono-i text-xs text-[#2f5561]">{newFact.confidence}%</span></div>
          <select value={newFact.access_level} onChange={(e) => setNewFact({ ...newFact, access_level: e.target.value })} className={inputCls + " max-w-[160px]"} data-testid="fact-access">
            {accessLevels.map((a) => <option key={a} value={a} className="bg-[#F6EEDC]">{a}</option>)}
          </select>
          <button onClick={addFact} data-testid="fact-add-btn" className="px-4 py-2 rounded-lg bg-[#F1551D]/15 border border-[#F1551D]/40 text-[#B84214] text-sm hover:bg-[#F1551D]/25">+ Add Fact</button>
        </div>
        <div className="space-y-2">
          {(p.facts || []).map((f) => (
            <div key={f.id} className="flex items-center justify-between gap-3 rounded-lg border border-[#F1551D]/12 bg-[#FEF7E7]/50 px-4 py-3">
              <div className="min-w-0">
                <span className="text-[11px] text-[#F1551D] font-mono-i">{f.module}</span>
                <div className="text-sm text-[#154859] truncate"><span className="text-[#8598a0]">{f.label}:</span> {f.value}</div>
                <div className="text-[11px] text-[#8598a0]">{f.source || "Unsourced"} · {f.confidence}% · {f.access_level}</div>
              </div>
              <button onClick={() => removeFact(f.id)} className="text-[#8598a0] hover:text-rose-400"><Icons.Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          {(p.facts || []).length === 0 && <p className="text-sm text-[#8598a0] text-center py-6">No facts yet. Add verified intelligence above.</p>}
        </div>
      </div>
    </div>
  );
}
